/**
 * Booked Camps Helper Functions
 * Handles CRUD operations for booked_camps JSONB column in profiles table
 * 
 * This module manages camp bookings stored in the profiles.booked_camps column.
 * Booked camps are separate from tracked programs and represent confirmed registrations.
 */

import { supabase, handleSupabaseError } from '@/lib/supabase';

// ============================================
// TYPES
// ============================================

export interface BookedCamp {
  id: string;
  name: string;
  location?: string;
  start_date: string;
  end_date?: string | null;
  child_name: string;
  notes?: string;
  price?: string;
  contact_info?: string;
  booked_at: string;
  status: 'booked' | 'completed' | 'cancelled';
}

export interface NewBookedCamp {
  id: string;
  name: string;
  location?: string;
  start_date?: string;
  end_date?: string | null;
  child_name: string;
  notes?: string;
  price?: string;
  contact_info?: string;
  booked_at: string;
  status: 'booked' | 'completed' | 'cancelled';
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Generates a new UUID for a booked camp
 * @private
 */
function generateBookedCampId(): string {
  return crypto.randomUUID();
}

/**
 * Creates a timestamp string for the current moment
 * @private
 */
function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Validates that a booked camp object has required fields
 * @private
 */
function validateBookedCamp(camp: Partial<BookedCamp>): boolean {
  console.log('validateBookedCamp - Checking camp data:', camp);
  console.log('validateBookedCamp - name:', camp.name);
  console.log('validateBookedCamp - start_date:', camp.start_date);
  console.log('validateBookedCamp - child_name:', camp.child_name);
  console.log('validateBookedCamp - status:', camp.status);
  
  const isValid = !!(
    camp.name &&
    camp.child_name &&
    camp.status
  );
  
  console.log('validateBookedCamp - Is valid:', isValid);
  return isValid;
}

// ============================================
// MAIN FUNCTIONS
// ============================================

/**
 * Retrieves all booked camps for a user from their profile
 * 
 * @param {string} userId - The UUID of the user
 * @returns {Promise<BookedCamp[]>} Array of booked camps
 * @throws {Error} If database query fails
 * 
 * @example
 * const bookedCamps = await getBookedCamps('user-uuid-here');
 * console.log(`Found ${bookedCamps.length} booked camps`);
 */
export async function getBookedCamps(userId: string): Promise<BookedCamp[]> {
  try {
    // Fetch current data
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('booked_camps')
      .eq('id', userId)
      .maybeSingle(); // Use maybeSingle instead of single

    // Handle case where profile doesn't exist yet
    if (fetchError) {
      // PGRST116 means no rows found - this is OK for new users
      if (fetchError.code === 'PGRST116') {
        console.log('Profile not found for user, returning empty array');
        return [];
      }
      console.error('Error in getBookedCamps:', fetchError);
      throw new Error(`Failed to fetch booked camps: ${fetchError.message}`);
    }

    // Return array (default to empty if null)
    return Array.isArray(profile?.booked_camps) ? profile.booked_camps : [];
  } catch (error) {
    console.error('Error in getBookedCamps:', error);
    throw error;
  }
}

/**
 * Get booked camps filtered by status
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} status - Camp status to filter by
 * @returns {Promise<BookedCamp[]>} Filtered booked camps
 */
export async function getBookedCampsByStatus(
  userId: string, 
  status: string
): Promise<BookedCamp[]> {
  try {
    const bookedCamps = await getBookedCamps(userId);
    return bookedCamps.filter(camp => camp.status === status);
  } catch (error) {
    console.error('Error in getBookedCampsByStatus:', error);
    throw error;
  }
}

/**
 * Get booked camps for a specific child
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} childName - Child's name to filter by
 * @returns {Promise<BookedCamp[]>} Booked camps for the specified child
 */
export async function getBookedCampsByChild(
  userId: string, 
  childName: string
): Promise<BookedCamp[]> {
  try {
    const bookedCamps = await getBookedCamps(userId);
    return bookedCamps.filter(camp => 
      camp.child_name.toLowerCase().includes(childName.toLowerCase())
    );
  } catch (error) {
    console.error('Error in getBookedCampsByChild:', error);
    throw error;
  }
}

/**
 * Get a single booked camp by ID
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} campId - The UUID of the booked camp
 * @returns {Promise<BookedCamp | null>} The booked camp or null if not found
 */
export async function getBookedCampById(
  userId: string, 
  campId: string
): Promise<BookedCamp | null> {
  try {
    const bookedCamps = await getBookedCamps(userId);
    return bookedCamps.find(camp => camp.id === campId) || null;
  } catch (error) {
    console.error('Error in getBookedCampById:', error);
    throw error;
  }
}

/**
 * Add a new booked camp to user's profile
 * 
 * @param {string} userId - The UUID of the user
 * @param {NewBookedCamp} camp - Camp data to add
 * @returns {Promise<BookedCamp>} The created booked camp with generated ID
 * @throws {Error} If validation fails or database update fails
 */
export async function addBookedCamp(
  userId: string, 
  camp: NewBookedCamp
): Promise<BookedCamp> {
  try {
    // Validate
    if (!validateBookedCamp(camp)) {
      throw new Error('Invalid booked camp data: missing required fields');
    }

    // Fetch current booked camps
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('booked_camps')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    // Prepare new booked camp
    const currentCamps = Array.isArray(profile?.booked_camps) ? profile.booked_camps : [];
    const newBookedCamp: BookedCamp = {
      id: camp.id || generateBookedCampId(),
      name: camp.name,
      location: camp.location || '',
      start_date: camp.start_date || getCurrentTimestamp(),
      end_date: camp.end_date || null,
      child_name: camp.child_name,
      notes: camp.notes || '',
      price: camp.price || '',
      contact_info: camp.contact_info || '',
      booked_at: camp.booked_at || getCurrentTimestamp(),
      status: camp.status || 'booked'
    };

    // Update database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        booked_camps: [...currentCamps, newBookedCamp],
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to add booked camp: ${updateError.message}`);
    }

    return newBookedCamp;
  } catch (error) {
    console.error('Error in addBookedCamp:', error);
    throw error;
  }
}

/**
 * Update an existing booked camp
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} campId - The UUID of the booked camp to update
 * @param {Partial<BookedCamp>} updates - Fields to update
 * @returns {Promise<BookedCamp>} The updated booked camp
 * @throws {Error} If camp not found or update fails
 */
export async function updateBookedCamp(
  userId: string, 
  campId: string, 
  updates: Partial<BookedCamp>
): Promise<BookedCamp> {
  try {
    // Fetch current booked camps
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('booked_camps')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    const currentCamps = Array.isArray(profile?.booked_camps) ? profile.booked_camps : [];
    const campIndex = currentCamps.findIndex(camp => camp.id === campId);

    if (campIndex === -1) {
      throw new Error('Booked camp not found');
    }

    // Update the camp
    const updatedCamps = [...currentCamps];
    updatedCamps[campIndex] = {
      ...updatedCamps[campIndex],
      ...updates,
      updated_at: getCurrentTimestamp()
    };

    // Update database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        booked_camps: updatedCamps,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to update booked camp: ${updateError.message}`);
    }

    return updatedCamps[campIndex];
  } catch (error) {
    console.error('Error in updateBookedCamp:', error);
    throw error;
  }
}

/**
 * Delete a booked camp
 * 
 * @param {string} userId - The UUID of the user
 * @param {string} campId - The UUID of the booked camp to delete
 * @returns {Promise<void>}
 * @throws {Error} If deletion fails
 */
export async function deleteBookedCamp(
  userId: string, 
  campId: string
): Promise<void> {
  try {
    // Fetch current booked camps
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('booked_camps')
      .eq('id', userId)
      .single();

    if (fetchError) {
      console.error('Error fetching profile:', fetchError);
      throw new Error(`Failed to fetch profile: ${fetchError.message}`);
    }

    const currentCamps = Array.isArray(profile?.booked_camps) ? profile.booked_camps : [];
    const updatedCamps = currentCamps.filter(camp => camp.id !== campId);

    // Update database
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ 
        booked_camps: updatedCamps,
        updated_at: getCurrentTimestamp()
      })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating profile:', updateError);
      throw new Error(`Failed to delete booked camp: ${updateError.message}`);
    }
  } catch (error) {
    console.error('Error in deleteBookedCamp:', error);
    throw error;
  }
}

/**
 * Count booked camps with optional filters
 * 
 * @param {string} userId - The UUID of the user
 * @param {Object} filters - Optional filters
 * @param {string} filters.status - Filter by status
 * @param {string} filters.childName - Filter by child name
 * @returns {Promise<number>} Count of booked camps matching filters
 */
export async function countBookedCamps(
  userId: string, 
  filters?: { status?: string; childName?: string }
): Promise<number> {
  try {
    let bookedCamps = await getBookedCamps(userId);

    if (filters) {
      if (filters.status) {
        bookedCamps = bookedCamps.filter(camp => camp.status === filters.status);
      }
      if (filters.childName) {
        bookedCamps = bookedCamps.filter(camp => 
          camp.child_name.toLowerCase().includes(filters.childName!.toLowerCase())
        );
      }
    }

    return bookedCamps.length;
  } catch (error) {
    console.error('Error in countBookedCamps:', error);
    throw error;
  }
}
